import * as React from "react";
import { useRef, useState } from "react";
import {
  Button,
  DialogContent,
  IconButton,
  Popover,
  styled,
} from "@mui/material";
import { KahunConfigurationManagementPage } from "./kahunConfigurationManagementPage";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { useConfigurationSettingsManager } from "../common/useConfigurationSettingsManager";
import clsx from "clsx";
import { ConfigurationSettingList } from "@kahunmedical/common-configuration/dist/lib/settings";
import { ConfigurationDottedKey } from "@kahunmedical/common-configuration/dist/lib/annotations";
import {
  KahunLocalStorageKey,
  readV2SettingsFromLocalStorage,
} from "../common/readV2SettingsFromLocalStorage";
import { RoleRestrictor } from "../../roles/RoleRestrictor";
import { GraphQlEnvPicker } from "../graphQlEnvPicker";
import WifiIcon from "@mui/icons-material/Wifi";
import CloseIcon from "@mui/icons-material/Close";
import { ClinicConfig } from "fe-common/graphql/clinic/clinicTypes";
import { UserRoles } from "fe-common/graphql";
import { ClinicDaoData } from "patient/src/clinic/clinicProvider";
import { MASTER_CLINIC_ID } from "fe-common/commonTypes/masterClinicDefaults";
//region [[ Styles ]]

const SettingsPageView = styled("div")({
  padding: "20px",
  backgroundColor: "#F7F7F9",
  height: "100%",
  width: "100%",
  boxSizing: "border-box",
  ...scrollableStyle,
  direction: "ltr", // Keep it in the right diraction no matter what
});

const TopArea = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-between",
});

const ClinicData = styled("div")({
  padding: "14px",
  fontSize: "12px",
  display: "flex",
  flexFlow: "column",
  alignItems: "flex-start",
});

const ClinicProperty = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "flex-start",
});

const ClinicPropertyName = styled("div")({
  color: "#5A52C3",
  fontWeight: 500,
  margin: "5px",
});

const ClinicPropertyValue = styled("div")({
  color: "#243443",
  fontWeight: 700,
});

const GraphQlSettingsButtonContainer = styled(Button)({
  padding: "10px",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  height: "fit-content",
  "& .MuiButton-label": {
    textTransform: "none",
    fontSize: "10px",
  },
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  right: "15px",
  top: "15px",
});

const GraphQlSettingsContent = styled(DialogContent)({
  paddingRight: "25px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SettingsPageProps {
  className?: string;
  clinicData: ClinicDaoData;
  onSave?: () => void;
  ignoreRoles?: boolean;
}

interface ClinicSettingPage {
  name?: string;
  clinic: string;
  config?: ClinicConfig;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]
export const LocalStorageSettingsPage = ({ ...props }: SettingsPageProps) => {
  const [localStorageData, setLocalStorageData] = useState<
    [string, ConfigurationSettingList]
  >(readV2SettingsFromLocalStorage);

  const settingsManager = useConfigurationSettingsManager(
    localStorageData,
    props.clinicData,
  );

  const saveToLocalStorage = (configToSave: ConfigurationSettingList) => {
    return new Promise<any>((resolve, reject) => {
      try {
        // Merge local changes with local storage
        const stringValue = JSON.stringify({
          ...localStorageData?.[1],
          ...configToSave,
        });

        localStorage.setItem(KahunLocalStorageKey, stringValue);

        setLocalStorageData([
          "localStorage",
          JSON.parse(stringValue) as ConfigurationSettingList,
        ]);

        if (props.onSave) props.onSave();
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });
  };

  const getAllNonDefault = () => {
    // Get current changes
    const currentChanges = settingsManager.settings.getNonDefaultSettings();
    // Return a merged object based on the current saved data
    return {
      ...localStorageData?.[1],
      ...currentChanges,
    } as ConfigurationSettingList;
  };

  // Delete local storage and re-initilize by reading localStorage data
  const handleResetDefault = (root: ConfigurationDottedKey) => {
    return new Promise<any>((resolve, reject) => {
      // Remove old object from localStorage
      localStorage.removeItem(KahunLocalStorageKey);
      // Also clear current local storage data from current root
      const newStorageValue = Object.entries(localStorageData?.[1]).reduce(
        (acc, [k, v]) => (k.startsWith(root) ? acc : { ...acc, [k]: v }),
        {},
      );

      setLocalStorageData([
        "localStorage",
        newStorageValue as ConfigurationSettingList,
      ]);

      // Save new object in local storage
      resolve(true);
    });
  };

  const [showGraphQlSettings, setShowGraphQlSettings] = useState(false);

  const graphEnvAnchor = useRef<HTMLButtonElement>(null);

  return (
    <RoleRestrictor
      roles={props.ignoreRoles ? [] : [UserRoles.KahunBackofficeUsers]}
      redirect
    >
      <SettingsPageView
        className={clsx(props.className, "local-storage-settings-page")}
      >
        <TopArea>
          <ClinicData className={"clinic-data"}>
            <ClinicProperty>
              <ClinicPropertyName> Current Clinic Name:</ClinicPropertyName>
              <ClinicPropertyValue>
                {props?.clinicData?.name}
              </ClinicPropertyValue>
            </ClinicProperty>
            <ClinicProperty>
              <ClinicPropertyName>id:</ClinicPropertyName>
              <ClinicPropertyValue>
                {props?.clinicData?.clinic}
              </ClinicPropertyValue>
            </ClinicProperty>
          </ClinicData>
          <GraphQlSettingsButtonContainer
            ref={graphEnvAnchor}
            onClick={() => setShowGraphQlSettings(true)}
            endIcon={<WifiIcon />}
            size="small"
          >
            Connection Settings
          </GraphQlSettingsButtonContainer>
        </TopArea>
        <Popover
          open={showGraphQlSettings}
          anchorEl={graphEnvAnchor.current}
          onClose={() => setShowGraphQlSettings(false)}
        >
          <GraphQlSettingsContent>
            <CloseButton onClick={() => setShowGraphQlSettings(false)}>
              <CloseIcon />
            </CloseButton>
            <GraphQlEnvPicker />
          </GraphQlSettingsContent>
        </Popover>
        <KahunConfigurationManagementPage
          configSaver={saveToLocalStorage}
          settingsManager={settingsManager}
          resetDefaults={handleResetDefault}
          getAllNonDefaultSettings={getAllNonDefault}
          isMasterClinic={props.clinicData.clinic === MASTER_CLINIC_ID}
        />
      </SettingsPageView>
    </RoleRestrictor>
  );
};
